import { useEffect, useState } from "react";
import { i18n as globalI18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  completeMfaSetup,
  enableMfa,
  initiateMfaSetup,
  type MfaSetupResponse,
} from "@/lib/auth";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

export function TwoFactorSetupDialog({
  open,
  onOpenChange,
  email,
  onEnabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onEnabled: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Mounted only while open; Radix unmounts on close so setup state
            resets between openings without a setState-in-effect reset. */}
        {open ? (
          <SetupBody
            email={email}
            onEnabled={onEnabled}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SetupBody({
  email,
  onEnabled,
  onClose,
}: {
  email: string;
  onEnabled: () => void;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");

  useEffect(() => {
    let active = true;
    initiateMfaSetup()
      .then((res) => {
        if (active) setSetup(res);
      })
      .catch((err: unknown) => {
        if (active) {
          setError(
            err instanceof Error
              ? err.message
              : globalI18n._(msg`Failed to start 2FA setup`),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const verifyMutation = useMutation({
    mutationFn: async (verificationCode: string) => {
      if (!setup?.session) {
        throw new Error(t`Setup session is not ready yet`);
      }
      await completeMfaSetup({
        code: verificationCode,
        session: setup.session,
        email,
      });
      await enableMfa();
    },
    onSuccess: () => {
      toast.success(t`Two-factor authentication enabled`);
      onEnabled();
      onClose();
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : t`Could not verify the code`,
      );
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle><Trans>Set up two-factor authentication</Trans></DialogTitle>
        <DialogDescription>
          <Trans>
            Scan the QR code with an authenticator app (Google Authenticator,
            1Password, …) — or enter the setup key manually — then enter the
            6-digit code it generates.
          </Trans>
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-(--color-text-secondary)">
          <Loader2 className="size-4 animate-spin" />
          <Trans>Preparing setup…</Trans>
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-(--color-danger-text)">{error}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {setup?.qrCodeUri ? (
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-xl border border-(--color-border) bg-(--color-surface-content) p-3">
                <QRCodeSVG value={setup.qrCodeUri} size={168} marginSize={2} />
              </div>
              <p className="text-xs text-(--color-text-secondary)">
                <Trans>Scan with your authenticator app</Trans>
              </p>
            </div>
          ) : null}

          {setup?.secretCode ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-(--color-text-primary)">
                <Trans>Can&rsquo;t scan? Enter this key manually</Trans>
              </span>
              <code className="break-all rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2 text-sm text-(--color-text-primary)">
                {setup.secretCode}
              </code>
            </div>
          ) : null}

          <form
            id="two-factor-form"
            onSubmit={(e) => {
              e.preventDefault();
              verifyMutation.mutate(code);
            }}
          >
            <Field label={t`Verification code`} htmlFor="mfa-code">
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                className="h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
            </Field>
          </form>
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={verifyMutation.isPending}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="submit"
          form="two-factor-form"
          disabled={
            verifyMutation.isPending || code.length !== 6 || !setup?.session
          }
        >
          {verifyMutation.isPending ? t`Verifying…` : t`Enable 2FA`}
        </Button>
      </DialogFooter>
    </>
  );
}
